import React, { useEffect } from 'react';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import { MAX_TABLE_LENGTH } from 'business/constants';
import { isEmptyChart } from 'helpers/chartHelper';
import { formatTableData } from 'helpers/dataHelpers/formatTableData';
import { formatVizData } from 'helpers/dataHelpers/formatVizData';
import { useChartConceptionContext } from 'routes/Explore/components/Contexts/ChartConceptionContext/ChartConceptionContext';
import {
    getAvailableViz,
    selectBestAvailableViz,
} from 'helpers/chart/availableVizHelpers';
import { Box } from '@mui/material';
import { ChartProps } from './types';
import DimensionedChart from './DimensionedChart';
import ChartMessage from './ChartMessage';
import { chartDisplaysNoData } from '../helpers';

import './_chart.scss';
import { useTranslation } from 'react-i18next';
import { DataTypes } from 'business/definitions';

/** The Chart component manages the chart data.
 * It performs pretty heavy calculations with formatTableData, so we should avoid rendering
 * it uselessly.
 * It calls DimensionedChart under the hood.
 */
const Chart = ({
    chartKey,
    applyChartSettings,
    settings,
    queryMetrics,
    data,
    columns,
    queryDimensions,
    vizType: vizTypeProp,
    shouldForceVizType = false,
    isPngModeActivated,
    showDetailsModal,
    isChartViewedOnDashboard,
    onFormatVizDataFailed,
    isArgument = false,
    timeFilters = undefined,
    queryFilters,
}: ChartProps): React.ReactElement => {
    //from dashboard, use EffectiveTimeFilters which overrides queryFilters, otherwise use queryFilter
    const effectiveTimeFilters =
        timeFilters ??
        queryFilters.filter((f) => 'type' in f && f.type === DataTypes.date);

    const { t } = useTranslation();
    const { setChartDimensions } = useChartConceptionContext();
    const availableViz = getAvailableViz(
        data,
        columns,
        queryMetrics,
        queryDimensions,
    );
    // In budget proposal view, we want to force pie chart even if it doesn't meet the criterias
    const vizType =
        shouldForceVizType && vizTypeProp
            ? vizTypeProp
            : selectBestAvailableViz(vizTypeProp, availableViz);
    const chartPadding = !['table', 'map'].includes(vizType);
    const isTable = vizType === 'table' || vizType === 'pivot_table';
    const paddingCSSClass = isTable ? 'table-chart-padding' : 'chart-padding';
    const CSSclass = chartPadding ? `chart ${paddingCSSClass}` : 'chart';

    const tableFormatResult = formatTableData({
        data,
        columns,
        settings,
        queryDimensions,
        queryMetrics,
        vizType,
        timeFilters: effectiveTimeFilters,
    });
    let chartFormatResult = { ...tableFormatResult, chartColumns: [] };

    if (!isTable && data.length <= MAX_TABLE_LENGTH) {
        try {
            chartFormatResult = formatVizData({
                data: tableFormatResult.chartData,
                columns: tableFormatResult.tableColumns,
                queryMetrics,
                settings,
                vizType,
            });
        } catch {
            onFormatVizDataFailed();
        }
    }
    useEffect(() => {
        // We populate the chart conception settings only if the columns are defined
        if (!Array.isArray(chartFormatResult.chartColumns)) {
            setChartDimensions(chartFormatResult.chartColumns);
        }
    }, [JSON.stringify(chartFormatResult.chartColumns)]);

    if (data.length > MAX_TABLE_LENGTH) {
        return (
            <ChartMessage>
                <p>{t('chart.tooMuchData')}</p>
            </ChartMessage>
        );
    }

    const { tableColumns } = tableFormatResult;
    const { chartData, chartColumns } = chartFormatResult;

    if (isEmptyChart(chartData) && !chartDisplaysNoData(vizType)) {
        return (
            <ChartMessage>
                <p>{t('components:common.noData')}</p>
            </ChartMessage>
        );
    }

    return (
        <Box
            id={`print-chart-key-${chartKey}`}
            className={CSSclass}
            sx={{
                height: isPngModeActivated ? '100%' : undefined,
            }}
        >
            <ErrorBoundary>
                <DimensionedChart
                    applyChartSettings={applyChartSettings}
                    chartKey={chartKey}
                    showDetailsModal={showDetailsModal}
                    data={chartData}
                    orderedColumns={isTable ? tableColumns : chartColumns}
                    vizType={vizType}
                    settings={settings}
                    isPngModeActivated={isPngModeActivated}
                    isChartViewedOnDashboard={isChartViewedOnDashboard}
                    isArgument={isArgument}
                />
            </ErrorBoundary>
        </Box>
    );
};

export default Chart;
